// Finance pricing queries and mutations. Every mutation refreshes all finance data and toasts its outcome.
import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { pricingService, type Catalog, type ListinoChanges, type NewListinoInput } from "../services/pricingService";
import type { CatalogItem, ListinoLineInput, ServiceLinePatch } from "../types";
import { dateLabel } from "../utils/format";
import { financeKeys, useFinanceMutation } from "./financeQuery";

export const useWorkTypes = () => useQuery({ queryKey: financeKeys.workTypes(), queryFn: pricingService.workTypes });
export const useServiceLines = () => useQuery({ queryKey: financeKeys.serviceLines(), queryFn: pricingService.serviceLines });
export const useListini = (enabled = true) => useQuery({ queryKey: financeKeys.listini(), queryFn: pricingService.listini, enabled });
export const useClientContracts = (clientId: string | undefined) =>
  useQuery({
    queryKey: financeKeys.clientContracts(clientId ?? "none"),
    queryFn: () => pricingService.clientContracts(clientId ?? ""),
    enabled: !!clientId,
  });

export const useCreateWorkType = () => useFinanceMutation((label: string) => pricingService.createWorkType(label));
export const useCreateServiceLine = () => useFinanceMutation((label: string) => pricingService.createServiceLine(label));
/** A service line's name and split defaults in one request (TD-078). */
export const useUpdateServiceLine = () =>
  useFinanceMutation((v: { id: string; patch: ServiceLinePatch }) => pricingService.updateServiceLine(v.id, v.patch), (row) => `${row.label} saved.`);

/**
 * PriceSplitEditor's "type a new work type": creates it in the catalog and resolves to its id once
 * the catalog has refetched. It rejects after the error toast, so callers must catch.
 */
export function useWorkTypeCreator() {
  const { mutateAsync } = useCreateWorkType();
  return useCallback((label: string) => mutateAsync(label).then((workType) => workType.id), [mutateAsync]);
}

/**
 * A new listino with the name and start the New listino modal set, in one request (TD-083): a refusal creates
 * nothing. Settles after the refetch, so the tab finds the new listino to select.
 */
export const useCreateListino = () =>
  useFinanceMutation((input: NewListinoInput) => pricingService.createListino(input), (l) => `${l.name} created, starting ${dateLabel(l.startDate)}.`);
export const useUpdateListino = () =>
  useFinanceMutation((v: { id: string; changes: ListinoChanges }) => pricingService.updateListino(v.id, v.changes), (l) => `${l.name} saved, starting ${dateLabel(l.startDate)}.`);
export const useDeleteListino = () => useFinanceMutation((id: string) => pricingService.deleteListino(id), () => `${ENTITY_CONFIG.listino.label} deleted.`);
export const useAddListinoLine = () =>
  useFinanceMutation((v: { id: string; line: ListinoLineInput }) => pricingService.addListinoLine(v.id, v.line), () => "Line added.");
export const useUpdateListinoLine = () =>
  useFinanceMutation((v: { id: string; lineId: string; line: ListinoLineInput }) => pricingService.updateListinoLine(v.id, v.lineId, v.line), () => "Line updated.");
export const useRemoveListinoLine = () =>
  useFinanceMutation((v: { id: string; lineId: string }) => pricingService.removeListinoLine(v.id, v.lineId), () => "Line removed.");

export const useRenameCatalogRow = () =>
  useFinanceMutation((v: { catalog: Catalog; id: string; label: string }) => pricingService.renameCatalogRow(v.catalog, v.id, v.label), (row) => `Renamed to ${row.label}.`);
export const useDeleteCatalogRow = () =>
  useFinanceMutation(
    (v: { catalog: Catalog; row: CatalogItem }) => pricingService.deleteCatalogRow(v.catalog, v.row.id).then(() => v.row),
    (row) => `${row.label} deleted.`,
    { settleOnResponse: true },
  );
